import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "orderedQuantity",
    "deliveredQuantity",
    "subTotal",
    "total",
    "orderSubTotal",
    "orderTotal",
    "finishProduct",
    "discountType",
    "discountValue",
    "discountAmount",
    "discountSymbol",
    "discountSavings",
    "discountTypeBadge",
    "orderDiscountAmount",
    "overridePrice",
    "defaultPriceDisplay"
  ]

  static values = {
    companyId: String
  }

  connect() {
    this.productPrices = new Map()
    this.initializeExistingRows()
    this.calculateTotals()
    this.initializeDiscountUI()
    this.initializeOrderedQuantityValues()

    // Direct fallback listeners for override price — bypasses Stimulus action routing
    this._overrideHandler = (e) => {
      console.log('[OIC] input fired on:', e.target.tagName, e.target.name, 'target attr:', e.target.dataset.orderItemCalculatorTarget)
      if (e.target && e.target.dataset.orderItemCalculatorTarget === 'overridePrice') {
        console.log('[OIC] overridePrice input matched, calling overridePriceChanged')
        this.overridePriceChanged(e)
      }
    }
    this.element.addEventListener('input', this._overrideHandler)
    this.element.addEventListener('change', this._overrideHandler)

    setTimeout(() => {
      this.calculateTotals()
    }, 100)
  }

  disconnect() {
    if (this._overrideHandler) {
      this.element.removeEventListener('input', this._overrideHandler)
      this.element.removeEventListener('change', this._overrideHandler)
    }
  }

  initializeExistingRows() {
    const companyId = this.element.dataset.companyId
    this.element.querySelectorAll('.nested-fields').forEach(row => {
      const select = row.querySelector('[data-order-item-calculator-target="finishProduct"]')
      const productId = select?.value
      if (!productId) return

      if (this.productPrices.has(productId)) {
        const price = this.productPrices.get(productId)
        row.dataset.defaultPrice = price
        const display = row.querySelector('[data-order-item-calculator-target="defaultPriceDisplay"]')
        if (display) display.textContent = `Default: Rs. ${parseFloat(price).toFixed(2)}`
      } else {
        fetch(`/api/v1/companies/${companyId}/finish_products/${productId}`)
          .then(r => r.json())
          .then(data => {
            this.productPrices.set(productId, data.price)
            row.dataset.defaultPrice = data.price
            const display = row.querySelector('[data-order-item-calculator-target="defaultPriceDisplay"]')
            if (display) display.textContent = `Default: Rs. ${parseFloat(data.price).toFixed(2)}`
          })
          .catch(() => {})
      }
    })
  }

  initializeOrderedQuantityValues() {
    // Initialize previous values for ordered quantity fields
    this.orderedQuantityTargets.forEach(field => {
      const value = parseFloat(field.value) || 0
      field.dataset.previousValue = value.toString()
    })
  }

  initializeDiscountUI() {
    // Set initial discount type if present - find checked radio button
    const checkedRadio = this.element.querySelector('input[type="radio"][name*="discount_type"]:checked') ||
                         this.element.querySelector('input[type="radio"][id*="discount_type"]:checked')
    if (checkedRadio) {
      this.updateDiscountUI(checkedRadio.value)
    }
  }

  async updateProductPrice(event) {
    const select = event.target
    const productId = select.value
    const rowElement = select.closest('.nested-fields')
    const companyId = this.element.dataset.companyId

    if (!productId) {
      this.updateRowTotals(rowElement, 0)
      this.calculateTotals()
      return
    }

    if (!this.productPrices.has(productId)) {
      try {
        const response = await fetch(`/api/v1/companies/${companyId}/finish_products/${productId}`)
        const data = await response.json()
        this.productPrices.set(productId, data.price)
      } catch (error) {
        console.error('Error fetching product price:', error)
        return
      }
    }

    const price = this.productPrices.get(productId)
    rowElement.dataset.defaultPrice = price
    const display = rowElement.querySelector('[data-order-item-calculator-target="defaultPriceDisplay"]')
    if (display) display.textContent = `Default: Rs. ${parseFloat(price).toFixed(2)}`
    this.updateRowTotals(rowElement, this.getEffectivePrice(rowElement))
    this.calculateTotals()
  }

  getEffectivePrice(rowElement) {
    const overrideInput = rowElement.querySelector('[data-order-item-calculator-target="overridePrice"]')
    const override = parseFloat(overrideInput?.value)
    if (!isNaN(override) && override > 0) return override

    // Use cached defaultPrice if available
    if (rowElement.dataset.defaultPrice) {
      return parseFloat(rowElement.dataset.defaultPrice)
    }

    // Fall back to fetching product price if not yet cached
    const finishProductSelect = rowElement.querySelector('[data-order-item-calculator-target="finishProduct"]')
    const productId = finishProductSelect?.value
    if (productId && this.productPrices.has(productId)) {
      return parseFloat(this.productPrices.get(productId))
    }

    return 0
  }

  overridePriceChanged(event) {
    console.log('[OIC] overridePriceChanged called, value:', event.target.value)
    const rowElement = event.target.closest('.nested-fields')
    if (!rowElement) { console.log('[OIC] no .nested-fields parent found'); return }
    const price = this.getEffectivePrice(rowElement)
    console.log('[OIC] effective price:', price)
    this.updateRowTotals(rowElement, price)
    this.calculateTotals()
  }

  calculateTotals() {
    let orderSubTotal = 0
    let orderTotal = 0

    // Calculate totals for each row
    document.querySelectorAll('.nested-fields').forEach(row => {
      const subTotalField = row.querySelector('[data-order-item-calculator-target="subTotal"]')
      const totalField = row.querySelector('[data-order-item-calculator-target="total"]')
      
      if (subTotalField && totalField) {
        orderSubTotal += parseFloat(subTotalField.value) || 0
        orderTotal += parseFloat(totalField.value) || 0
      }
    })

    // Update order subtotal
    if (this.hasOrderSubTotalTarget) {
      this.orderSubTotalTarget.value = orderSubTotal.toFixed(2)
    }

    // Calculate discount and update total
    const discountAmount = this.calculateDiscount(orderSubTotal)
    const finalTotal = orderSubTotal - discountAmount

    // Update discount amount display
    if (this.hasDiscountAmountTarget) {
      this.discountAmountTarget.value = discountAmount.toFixed(2)
    }
    if (this.hasOrderDiscountAmountTarget) {
      this.orderDiscountAmountTarget.value = discountAmount.toFixed(2)
    }

    // Update badge - show if discount is applied
    if (this.hasDiscountTypeBadgeTarget) {
      const checkedRadio = this.element.querySelector('input[type="radio"][name*="discount_type"]:checked') ||
                           this.element.querySelector('input[type="radio"][id*="discount_type"]:checked')
      if (checkedRadio && discountAmount > 0) {
        this.discountTypeBadgeTarget.textContent = checkedRadio.value === 'percentage' ? '%' : '₹'
        this.discountTypeBadgeTarget.style.display = 'inline'
      } else {
        this.discountTypeBadgeTarget.style.display = 'none'
      }
    }

    // Update savings message
    if (this.hasDiscountSavingsTarget) {
      if (discountAmount > 0) {
        const savingsSpan = this.discountSavingsTarget.querySelector('span')
        if (savingsSpan) {
          savingsSpan.textContent = `₹${discountAmount.toFixed(2)}`
        }
        this.discountSavingsTarget.style.display = 'block'
      } else {
        this.discountSavingsTarget.style.display = 'none'
      }
    }

    // Update order total
    if (this.hasOrderTotalTarget) {
      this.orderTotalTarget.value = finalTotal.toFixed(2)
    }
  }

  calculateDiscount(subTotal) {
    if (!this.hasDiscountValueTarget) {
      return 0
    }

    // Find the checked discount type radio button
    const checkedRadio = this.element.querySelector('input[type="radio"][name*="discount_type"]:checked') ||
                         this.element.querySelector('input[type="radio"][id*="discount_type"]:checked')
    if (!checkedRadio) {
      return 0
    }

    const discountType = checkedRadio.value
    const discountValue = parseFloat(this.discountValueTarget.value) || 0

    if (!discountType || discountValue <= 0 || subTotal <= 0) {
      return 0
    }

    let discountAmount = 0

    if (discountType === 'percentage') {
      // Percentage discount: value is 0-100
      if (discountValue > 100) {
        return 0 // Invalid, will be caught by validation
      }
      discountAmount = (subTotal * discountValue) / 100
    } else if (discountType === 'amount') {
      // Fixed amount discount: value is the discount amount
      discountAmount = Math.min(discountValue, subTotal) // Don't exceed subtotal
    }

    return discountAmount
  }

  discountTypeChanged(event) {
    const discountType = event.target.value
    this.updateDiscountUI(discountType)
    
    // Clear discount value when type changes
    if (this.hasDiscountValueTarget) {
      this.discountValueTarget.value = ''
    }
    
    // Recalculate totals
    setTimeout(() => {
      this.calculateTotals()
    }, 10)
  }

  discountValueChanged() {
    setTimeout(() => {
      this.calculateTotals()
    }, 10)
  }

  updateDiscountUI(discountType) {
    if (!discountType) {
      return
    }

    // Update symbol
    if (this.hasDiscountSymbolTarget) {
      this.discountSymbolTarget.textContent = discountType === 'percentage' ? '%' : '₹'
    }

    // Update placeholder
    if (this.hasDiscountValueTarget) {
      this.discountValueTarget.placeholder = discountType === 'percentage' ? '0.00' : '0.00'
      this.discountValueTarget.step = '0.01'
      this.discountValueTarget.max = discountType === 'percentage' ? '100' : ''
    }

    // Update badge
    if (this.hasDiscountTypeBadgeTarget) {
      if (discountType) {
        this.discountTypeBadgeTarget.textContent = discountType === 'percentage' ? '%' : '₹'
        this.discountTypeBadgeTarget.style.display = 'inline'
      } else {
        this.discountTypeBadgeTarget.style.display = 'none'
      }
    }
  }

  updateRowTotals(rowElement, price) {
    const deliveredQuantityField = rowElement.querySelector('[data-order-item-calculator-target="deliveredQuantity"]')
    const orderedQuantityField = rowElement.querySelector('[data-order-item-calculator-target="orderedQuantity"]')
    const subTotalField = rowElement.querySelector('[data-order-item-calculator-target="subTotal"]')
    const totalField = rowElement.querySelector('[data-order-item-calculator-target="total"]')

    // Get the delivered and ordered quantities
    const deliveredQuantity = deliveredQuantityField?.value !== '' && deliveredQuantityField?.value !== null && deliveredQuantityField?.value !== undefined
      ? parseFloat(deliveredQuantityField.value) || 0
      : null
    const orderedQuantity = parseFloat(orderedQuantityField?.value) || 0

    // Use delivered quantity if it's explicitly set (including 0), otherwise use ordered quantity
    const quantityToUse = deliveredQuantity !== null ? deliveredQuantity : orderedQuantity
    const subTotal = quantityToUse * price

    if (subTotalField) subTotalField.value = subTotal.toFixed(2)
    if (totalField) totalField.value = subTotal.toFixed(2)
  }

  updateQuantity(event) {
    const rowElement = event.target.closest('.nested-fields')
    const finishProductSelect = rowElement.querySelector('[data-order-item-calculator-target="finishProduct"]')
    const productId = finishProductSelect?.value
    const companyId = this.element.dataset.companyId

    if (!productId) {
      this.updateRowTotals(rowElement, 0)
      this.calculateTotals()
      return
    }

    if (!this.productPrices.has(productId)) {
      fetch(`/api/v1/companies/${companyId}/finish_products/${productId}`)
        .then(response => response.json())
        .then(data => {
          this.productPrices.set(productId, data.price)
          rowElement.dataset.defaultPrice = data.price
          const display = rowElement.querySelector('[data-order-item-calculator-target="defaultPriceDisplay"]')
          if (display) display.textContent = `Default: Rs. ${parseFloat(data.price).toFixed(2)}`
          this.updateRowTotals(rowElement, this.getEffectivePrice(rowElement))
          this.calculateTotals()
        })
        .catch(error => {
          console.error('Error fetching product price:', error)
        })
    } else {
      this.updateRowTotals(rowElement, this.getEffectivePrice(rowElement))
      this.calculateTotals()
    }
  }

  updateOrderedQuantity(event) {
    const rowElement = event.target.closest('.nested-fields')
    const orderedQuantityField = event.target
    const deliveredQuantityField = rowElement.querySelector('[data-order-item-calculator-target="deliveredQuantity"]')
    const orderedQuantity = parseFloat(orderedQuantityField.value) || 0

    // Validate ordered quantity is greater than zero
    if (orderedQuantity <= 0) {
      orderedQuantityField.setCustomValidity('Ordered quantity must be greater than zero')
      orderedQuantityField.reportValidity()
      return
    } else {
      orderedQuantityField.setCustomValidity('')
    }

    // Update delivered quantity to match ordered quantity if it hasn't been manually changed
    // Only update if delivered quantity equals the previous ordered quantity (default state)
    if (deliveredQuantityField) {
      const currentDeliveredQuantity = parseFloat(deliveredQuantityField.value) || 0
      const previousOrderedQuantity = parseFloat(orderedQuantityField.dataset.previousValue) || 0
      
      // If delivered quantity matches previous ordered quantity, update it to new ordered quantity
      if (Math.abs(currentDeliveredQuantity - previousOrderedQuantity) < 0.01) {
        deliveredQuantityField.value = orderedQuantity.toFixed(2)
      }
      
      // Store current value for next comparison
      orderedQuantityField.dataset.previousValue = orderedQuantity.toString()
    }

    // Recalculate totals - use the same logic as updateQuantity
    const finishProductSelect = rowElement.querySelector('[data-order-item-calculator-target="finishProduct"]')
    const productId = finishProductSelect?.value
    const companyId = this.element.dataset.companyId

    if (!productId) {
      this.updateRowTotals(rowElement, 0)
      this.calculateTotals()
      return
    }

    if (!this.productPrices.has(productId)) {
      fetch(`/api/v1/companies/${companyId}/finish_products/${productId}`)
        .then(response => response.json())
        .then(data => {
          this.productPrices.set(productId, data.price)
          rowElement.dataset.defaultPrice = data.price
          const display = rowElement.querySelector('[data-order-item-calculator-target="defaultPriceDisplay"]')
          if (display) display.textContent = `Default: Rs. ${parseFloat(data.price).toFixed(2)}`
          this.updateRowTotals(rowElement, this.getEffectivePrice(rowElement))
          this.calculateTotals()
        })
        .catch(error => {
          console.error('Error fetching product price:', error)
        })
    } else {
      this.updateRowTotals(rowElement, this.getEffectivePrice(rowElement))
      this.calculateTotals()
    }
  }

  removeItem() {
    setTimeout(() => this.calculateTotals(), 100)
  }

  updateTotalWithDiscount() {
    this.calculateTotals()
  }
}
